import { Alert, Input, InputNumber, message, Select, Space, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import Card from '../Card';
import useAPIQuery from '../api/useAPIQuery';
import {
  JobPostingDetail,
  SankhyaCompany,
  SankhyaDepartment,
  SankhyaJobRole,
  SeniorityLevelCollectionData,
  VixtingAffiliate,
  VixtingUnitBusiness,
} from '../types';
import { UserCollectionSerializer, UserDetailSerializer } from '../types/api';
import { currencyFormatter, currencyParser, getFrontendManualTranslations } from '../utils';
import Form from './Form';

interface GeneralJobPostingFormProps {
  enableIntegrationAdmission?: boolean;
  sankhyaCompanies: SankhyaCompany[];
  sankhyaJobRoles: SankhyaJobRole[];
  sankhyaDepartments: SankhyaDepartment[];
  recruiterUserOptions?: UserCollectionSerializer[];
  managerUserOptions?: UserCollectionSerializer[];
  relatedUserOptions?: UserCollectionSerializer[];
  observersUserOptions?: UserCollectionSerializer[];
  user?: UserDetailSerializer;
  affiliates?: VixtingAffiliate[];
  unitBusinesses?: VixtingUnitBusiness[];
  seniority_levels: SeniorityLevelCollectionData[];
  enableChangeLanguage: boolean;
}

export interface GeneralJobPostingFormValues {
  name: string;
  positions: number;
  startSalaryRange?: string | null;
  endSalaryRange?: string | null;
  limitDays: number;
  sankhyaJobRole?: string;
  sankhyaCompany?: string;
  sankhyaDepartment?: string;
  recruiter?: number;
  manager?: number;
  related?: number[];
  observers?: number[];
  affiliate?: string;
  unitBusiness?: string;
  seniorityLevel?: number | null;
  workModel?: JobPostingDetail['work_model'];
  hireModel?: JobPostingDetail['hire_model'];
}

const HIRE_MODEL_LIST_LENGTH = 9;
const SELECT_LIST_ITEM_HEIGHT = 32;

const GeneralJobPostingForm = ({
  enableIntegrationAdmission,
  sankhyaCompanies,
  sankhyaJobRoles,
  sankhyaDepartments,
  recruiterUserOptions,
  managerUserOptions,
  relatedUserOptions,
  observersUserOptions,
  user,
  affiliates = [],
  unitBusinesses = [],
  seniority_levels = [],
  enableChangeLanguage,
}: GeneralJobPostingFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<GeneralJobPostingFormValues>();

  const {
    data: { enableVixtingAdmission, enableVixtingUnitBusiness } = {
      enableVixtingAdmission: false,
      enableVixtingUnitBusiness: false,
    },
  } = useAPIQuery('/configs/tenant-configurations/');

  const selectedCompany = Form.useWatch('sankhyaCompany', form);
  const selectedJobRole = Form.useWatch('sankhyaJobRole', form);
  const recruiterValue = Form.useWatch('recruiter', form);
  const managerValue = Form.useWatch('manager', form);
  const relatedValue = Form.useWatch('related', form);
  const endSalaryRangeValue = Form.useWatch<number | string | undefined>('endSalaryRange', form);

  const validateSalaryRange = async () => {
    try {
      await form.validateFields(['startSalaryRange']);
    } catch (err) {
      console.warn(
        'O valor inicial da faixa salarial não pode ser maior ou igual que o valor final.'
      );
    }
  };

  const resetCompanyRelatedFields = async (selectedCompany: string) => {
    enableVixtingAdmission &&
      form.setFieldsValue({
        affiliate: selectedCompany,
        unitBusiness: undefined,
        sankhyaJobRole: undefined,
        sankhyaDepartment: undefined,
      });
  };
  const resetJobRoleRelatedFields = async () => {
    enableVixtingAdmission &&
      form.setFieldsValue({
        sankhyaDepartment: undefined,
      });
  };

  return (
    <Space direction="vertical" size={16}>
      <Card>
        <Form.Legend>
          <Trans>Dados gerais da vaga</Trans>
        </Form.Legend>
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item name="name" label={t('Nome da vaga')} rules={[{ required: true }]}>
              <Input placeholder={t('Ex: Desenvolvedor de Software')} />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item
              name="seniorityLevel"
              label={t('Nível de Senioridade')}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                filterSort={(optionA, optionB) =>
                  ((optionA?.id as unknown as string) ?? '').localeCompare(
                    (optionB?.id as unknown as string) ?? ''
                  )
                }
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                data-testid="select-seniority-level"
              >
                {seniority_levels.map(({ id: seniorityLevelID, key, level }) => (
                  <Select.Option
                    key={key}
                    value={seniorityLevelID}
                    label={getFrontendManualTranslations(level, enableChangeLanguage)}
                    data-testid={`select-seniority-level-${seniorityLevelID}`}
                  >
                    {getFrontendManualTranslations(level, enableChangeLanguage)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item
              name="positions"
              label={t('Quantidade de posições')}
              rules={[{ required: true }, { type: 'number', min: 1 }]}
            >
              <InputNumber min={1} max={2147483647} style={{ width: '100%' }} />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item
              name="limitDays"
              label={t('Em quantos dias deseja finalizar a vaga?')}
              rules={[{ required: true }, { type: 'number', min: 1 }]}
            >
              <InputNumber min={1} max={2147483647} style={{ width: '100%' }} />
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item name="hireModel" label={t('Modelo de contratação')}>
              <Select
                allowClear
                data-testid="select-hire-model"
                placeholder={t('Selecione o modelo')}
                listHeight={HIRE_MODEL_LIST_LENGTH * SELECT_LIST_ITEM_HEIGHT}
                options={[
                  {
                    key: 'TALENT_POOL',
                    value: 'TALENT_POOL',
                    label: t('Banco de Talentos'),
                  },
                  {
                    key: 'APRENDICE',
                    value: 'APRENDICE',
                    label: t('Aprendiz'),
                  },
                  {
                    key: 'INTERNSHIP',
                    value: 'INTERNSHIP',
                    label: t('Estágio'),
                  },
                  {
                    key: 'AUTONOMOUS_PJ',
                    value: 'AUTONOMOUS_PJ',
                    label: t('Autônomo PJ'),
                  },
                  {
                    key: 'TRAINEE',
                    value: 'TRAINEE',
                    label: t('Trainee'),
                  },
                  {
                    key: 'TEMPORARY',
                    value: 'TEMPORARY',
                    label: t('Temporário'),
                  },
                  {
                    key: 'EFFECTIVE_CLT',
                    value: 'EFFECTIVE_CLT',
                    label: t('Efetivo CLT'),
                  },
                  {
                    key: 'COOPERATED',
                    value: 'COOPERATED',
                    label: t('Cooperado'),
                  },
                  {
                    key: 'ASSOCIATED',
                    value: 'ASSOCIATED',
                    label: t('Associado'),
                  },
                ]}
              />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item name="workModel" label={t('Modelo de trabalho')}>
              <Select
                allowClear
                data-testid="select-work-model"
                placeholder={t('Selecione o modelo')}
                options={[
                  {
                    key: 'IN_PERSON',
                    value: 'IN_PERSON',
                    label: t('Presencial'),
                  },
                  {
                    key: 'HYBRID',
                    value: 'HYBRID',
                    label: t('Híbrido'),
                  },
                  {
                    key: 'REMOTE',
                    value: 'REMOTE',
                    label: t('Remoto'),
                  },
                ]}
              />
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <Form.Item label={t('Faixa salarial')}>
          <Form.Row>
            <Form.Col span={12}>
              <Form.Item
                name="startSalaryRange"
                noStyle
                rules={[
                  {
                    validator(_, value: number | undefined) {
                      if (!value && !endSalaryRangeValue) {
                        return Promise.resolve();
                      }

                      if (value && value < 0) {
                        return Promise.reject(
                          new Error(t('O salário inicial deve ser maior que 0,00'))
                        );
                      }

                      if (endSalaryRangeValue && Number(endSalaryRangeValue) < 1) {
                        return Promise.reject(
                          new Error(t('O salário final deve ser maior que 1,00'))
                        );
                      }

                      // if no value was inserted, it is valid
                      if (
                        value === undefined ||
                        (endSalaryRangeValue && value < Number(endSalaryRangeValue))
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t(
                            'O valor inicial da faixa salarial não pode ser maior ou igual que o valor final.'
                          )
                        )
                      );
                    },
                  },
                ]}
              >
                <InputNumber<string | number>
                  prefix={t('R$')}
                  style={{ width: '100%' }}
                  max={2147483647}
                  placeholder={t('Mínimo')}
                  step={100}
                  formatter={(value) => currencyFormatter(value)}
                  parser={(displayValue) => currencyParser(displayValue)}
                  onChange={validateSalaryRange}
                  stringMode
                />
              </Form.Item>
            </Form.Col>
            <Form.Col span={12}>
              <Form.Item name="endSalaryRange" noStyle>
                <InputNumber<string | number>
                  prefix={t('R$')}
                  style={{ width: '100%' }}
                  max={2147483647}
                  step={100}
                  placeholder={t('Máximo')}
                  formatter={(value) => currencyFormatter(value)}
                  parser={(displayValue) => currencyParser(displayValue)}
                  onChange={validateSalaryRange}
                  stringMode
                />
              </Form.Item>
            </Form.Col>
          </Form.Row>
        </Form.Item>
      </Card>
      <Card>
        <Form.Legend>{t('Time de recrutamento')}</Form.Legend>
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item name="recruiter" label={t('Recrutador(a) responsável')}>
              <Select
                allowClear
                showSearch
                showArrow
                data-testid="recruiter-select"
                filterOption={(input, option) =>
                  (option?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t('Selecione a pessoa recrutadora')}
                options={(recruiterUserOptions || []).map(({ id, full_name, username }) => ({
                  key: id,
                  label: full_name || username,
                  value: id,
                  'data-testid': `recruiter-select-${id}`,
                }))}
              />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item name="manager" label={t('Gestor(a) da vaga')}>
              <Select
                allowClear
                showSearch
                showArrow
                data-testid="manager-select"
                filterOption={(input, option) =>
                  (option?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t('Selecione a pessoa gestora')}
                options={(managerUserOptions || [])
                  .filter((user) => user.id !== recruiterValue)
                  .map(({ id, full_name, username }) => ({
                    key: id,
                    label: full_name || username,
                    value: id,
                    'data-testid': `manager-select-${id}`,
                  }))}
              />
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item name="related" label={t('Envolvidos')}>
              <Select
                allowClear
                mode="multiple"
                showSearch
                showArrow
                data-testid="related-select"
                filterOption={(input, option) =>
                  (option?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t('Selecione as pessoas envolvidas')}
                options={(relatedUserOptions || [])
                  .filter((user) => user.id !== recruiterValue && user.id !== managerValue)
                  .map(({ id, full_name, username }) => ({
                    key: id,
                    label: full_name || username,
                    value: id,
                    'data-testid': `related-select-${id}`,
                  }))}
              />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item
              name="observers"
              label={t('Observadores')}
              tooltip={
                <span>
                  <Trans>
                    Usuários marcados nesse campo poderão apenas visualizar a vaga. Eles
                  </Trans>{' '}
                  <Typography.Text style={{ color: 'inherit' }} strong>
                    <Trans>
                      não terão permissão para realizar ações, somente registrar comentários
                    </Trans>
                  </Typography.Text>
                </span>
              }
            >
              <Select
                allowClear
                mode="multiple"
                showSearch
                showArrow
                data-testid="observer-select"
                filterOption={(input, option) =>
                  (option?.label || '').toLowerCase().includes(input.toLowerCase())
                }
                onSelect={(value) => {
                  if (value === user?.id) {
                    message.error(t('Você não pode se marcar como observador'));
                    form.setFieldValue(
                      'observers',
                      form.getFieldValue('observers').filter((id: number) => id !== user?.id)
                    );
                  }
                }}
                placeholder={t('Selecione as pessoas observadoras')}
                options={(observersUserOptions || [])
                  .filter((user) => {
                    return (
                      user.id !== recruiterValue &&
                      user.id !== managerValue &&
                      !((relatedValue || []) as number[]).find((id) => id === user.id)
                    );
                  })
                  .map(({ id, full_name, username }) => ({
                    key: id,
                    label: full_name || username,
                    value: id,
                    'data-testid': `observer-select-${id}`,
                  }))}
              />
            </Form.Item>
          </Form.Col>
        </Form.Row>
      </Card>
      {enableIntegrationAdmission && (
        <Card>
          <Form.Legend>
            <Trans>Alocação da vaga</Trans>
          </Form.Legend>
          <Form.Row>
            <Form.Col span={12}>
              <Form.Item name="sankhyaCompany" label={t('Empresa')}>
                <Select
                  showSearch
                  filterSort={(optionA, optionB) =>
                    ((optionA?.label as unknown as string) ?? '').localeCompare(
                      (optionB?.label as unknown as string) ?? ''
                    )
                  }
                  filterOption={(input, option) =>
                    (option?.label as unknown as string).toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder={t('Selecione a empresa')}
                  onChange={resetCompanyRelatedFields}
                  data-testid="select-sankhya-company"
                >
                  {sankhyaCompanies.map(({ CODEMP, Empresa_NOMEFANTASIA }) => (
                    <Select.Option
                      key={CODEMP}
                      value={`${CODEMP}`}
                      label={`${Empresa_NOMEFANTASIA}`}
                      data-testid={`select-sankhya-company-${CODEMP}`}
                    >
                      <span title={CODEMP}>{`${Empresa_NOMEFANTASIA}`}</span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form.Col>
            {enableVixtingAdmission && (
              <Form.Col span={12}>
                <Form.Item name="affiliate" label={t('Filial')}>
                  <Select
                    showSearch
                    filterSort={(optionA, optionB) =>
                      ((optionA?.label as unknown as string) ?? '').localeCompare(
                        (optionB?.label as unknown as string) ?? ''
                      )
                    }
                    filterOption={(input, option) =>
                      (option?.label as unknown as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    disabled={!selectedCompany}
                    placeholder={t('Selecione a filial')}
                    data-testid="select-vixting-affiliate"
                  >
                    {affiliates.map((affiliate) => (
                      <Select.Option
                        key={affiliate.id}
                        value={`${affiliate.id}`}
                        data-testid={`select-vixting-affiliate-${affiliate.id}`}
                        label={`${affiliate.fantasyName}`}
                      >
                        <span title={affiliate.id}>{`${affiliate.fantasyName}`}</span>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form.Col>
            )}
          </Form.Row>
          {enableVixtingAdmission && enableVixtingUnitBusiness && (
            <Form.Row>
              <Form.Col span={24}>
                <Form.Item name="unitBusiness" label={t('Unidade de Negócio')}>
                  <Select
                    showSearch
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').localeCompare(optionB?.label ?? '')
                    }
                    filterOption={(input, option) =>
                      (option?.label as string).toLowerCase().includes(input.toLowerCase())
                    }
                    disabled={!selectedCompany}
                    placeholder={t('Selecione a Unidade de Negócio')}
                    data-testid="select-vixting-unitBusiness"
                    options={unitBusinesses.map((unitBusiness) => ({
                      'data-testid': `select-vixting-unitBusiness-${unitBusiness.id}`,
                      value: `${unitBusiness.id}`,
                      label: unitBusiness.name,
                      key: unitBusiness.id,
                    }))}
                  />
                </Form.Item>
              </Form.Col>
            </Form.Row>
          )}
          <Form.Row>
            <Form.Col span={12}>
              <Form.Item name="sankhyaJobRole" label={t('Cargo')}>
                <Select
                  showSearch
                  disabled={enableVixtingAdmission && !selectedCompany}
                  filterSort={(optionA, optionB) =>
                    ((optionA?.label as unknown as string) ?? '').localeCompare(
                      (optionB?.label as unknown as string) ?? ''
                    )
                  }
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder={t('Selecione o cargo')}
                  onChange={resetJobRoleRelatedFields}
                  data-testid="select-sankhya-job-role"
                >
                  {sankhyaJobRoles.map(({ CODCARGO, DESCRCARGO }) => (
                    <Select.Option key={CODCARGO} value={`${CODCARGO}`} label={`${DESCRCARGO}`}>
                      {`${CODCARGO} - ${DESCRCARGO}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form.Col>
            <Form.Col span={12}>
              <Form.Item name="sankhyaDepartment" label={t('Departamento')}>
                <Select
                  showSearch
                  disabled={enableVixtingAdmission && !selectedJobRole}
                  filterSort={(optionA, optionB) =>
                    ((optionA?.label as unknown as string) ?? '').localeCompare(
                      (optionB?.label as unknown as string) ?? ''
                    )
                  }
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  placeholder={t('Selecione o departamento')}
                  data-testid="select-sankhya-department"
                >
                  {sankhyaDepartments.map(({ CODDEP, DESCRDEP }) => (
                    <Select.Option key={CODDEP} value={`${CODDEP}`} label={`${DESCRDEP}`}>
                      {`${CODDEP} - ${DESCRDEP}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form.Col>
          </Form.Row>
          {enableVixtingAdmission && (
            <Form.Row>
              <Form.Col span={24}>
                <Alert
                  message={
                    <span style={{ fontSize: '13px' }}>
                      <Trans>
                        Caso o cargo ou departamento desejado não esteja disponível, entre em
                        contato com o time de SST da Vixting pelo e-mail
                      </Trans>{' '}
                      <a
                        href="mailto:sst@vixting.com.br?subject=Solicitação de vinculação de cargo/departamento"
                        style={{ fontSize: '13px' }}
                      >
                        sst@vixting.com.br
                      </a>{' '}
                      <Trans>para solicitar a vinculação.</Trans>
                    </span>
                  }
                  type="info"
                  showIcon
                  closable
                />
              </Form.Col>
            </Form.Row>
          )}
        </Card>
      )}
    </Space>
  );
};

export default GeneralJobPostingForm;
